<script setup>
import * as slider from '@zag-js/slider'
import { normalizeProps, useMachine } from '@zag-js/vue'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const api = computed(() => slider.connect(service, normalizeProps))
const props = defineProps({
  type: {
    type: String,
    default: 'range'
  },
  labelList: {
    type: Array,
    default: () => []
  }
})
const sliderValue = defineModel('sliderValue', {
  type: Number,
  required: true
})

const route = useRoute()
const isFirstDay = () => {
  return `${route.params.year}/${route.params.month}/${route.params.day}` === `${pastPageStartYear}/01/01`
}
const isLastDay = () => {
  const { endYear, endMonth, endDay } = getEndYearMonthDay()
  return `${route.params.year}/${route.params.month}/${route.params.day}` === `${endYear}/${String(endMonth).padStart(2, '0')}/${String(endDay).padStart(2, '0')}`
}
const minIndex = ref(0)
const maxIndex = ref(props.labelList.length - 1)
const { isPlaying, clearId, onClickPlay, onClickStop } = useAnimate(sliderValue, maxIndex)
const { rangeInput, initTouchEvents, removeTouchEvents } = customMapSlider(
  sliderValue,
  minIndex,
  maxIndex
)
const isFirstItem = computed(() => sliderValue.value === 0)
const isLastItem = computed(() => sliderValue.value === maxIndex.value)

const selectItem = (index) => {
  if (index >= 0 && index < props.labelList.length) {
    api.value.setValue([index])
  }
}
const onClickNext = () => {
  selectItem(sliderValue.value + 1)
}
const onClickPrev = () => {
  selectItem(sliderValue.value - 1)
}
const onClickNextDay = () => {
  const year = Number(route.params.year)
  const month = Number(route.params.month)
  const day = Number(route.params.day)
  if (!year || !month || !day) return
  const nextDay = dayjs(`${year}-${month}-${day}`).add(1, 'day')
  return navigateTo(`/satellite/${dayjs(nextDay).format('YYYY/MM/DD')}/`)
}
const onClickPrevDay = () => {
  const year = Number(route.params.year)
  const month = Number(route.params.month)
  const day = Number(route.params.day)
  if (!year || !month || !day) return
  const prevDay = dayjs(`${year}-${month}-${day}`).subtract(1, 'day')
  return navigateTo(`/satellite/${dayjs(prevDay).format('YYYY/MM/DD')}/`)
}

const outputVisible = ref(true)
let hideTimer = null
const showOutput = () => {
  outputVisible.value = true
  if (hideTimer) clearTimeout(hideTimer)
}
const scheduleHide = () => {
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = setTimeout(() => {
    outputVisible.value = false
  }, 1500)
}

const service = useMachine(slider.machine, {
  id: 1,
  defaultValue: [sliderValue.value ?? minIndex.value],
  min: minIndex.value,
  max: maxIndex.value,
  step: 1,
  thumbAlignment: 'center',
  onValueChange: (details) => {
    sliderValue.value = details.value[0]
  }
})

watch(sliderValue, (newVal) => {
  api.value.setValue([newVal])
})

const shouldShowLabel = (index) => index % 3 === 0

onMounted(() => {
  initTouchEvents()
})

onUnmounted(() => {
  clearTimeout(clearId.value)
  removeTouchEvents()
})
</script>

<template>
  <div class="slider__inner">
    <div class="controller">
      <div class="controller__button">
        <button
          data-button="prevDay"
          :disabled="isFirstDay()"
          @click="onClickPrevDay"
        />
        <button
          data-button="prev"
          :disabled="isFirstItem"
          @click="onClickPrev()"
        />
        <p>{{ dayjs.unix(labelList[sliderValue]).tz('Asia/Tokyo').format('M/D (ddd) H:mm') }}</p>
        <button
          data-button="next"
          :disabled="isLastItem"
          @click="onClickNext()"
        />
        <button
          data-button="nextDay"
          :disabled="isLastDay()"
          @click="onClickNextDay"
        />
      </div>
    </div>
    <div class="content">
      <div class="anime__button">
        <button
          :data-button="isPlaying ? 'stop': 'play'"
          @click="isPlaying ? onClickStop() : onClickPlay()"
        />
      </div>
      <div class="range">
        <div v-bind="api.getRootProps()">
          <div
            ref="rangeInput"
            v-bind="api.getControlProps()"
          >
            <div v-bind="api.getTrackProps()">
              <div v-bind="api.getRangeProps()" />
            </div>
            <div
              v-for="(_, index) in api.value"
              :key="index"
              v-bind="api.getThumbProps({ index })"
              @pointerdown="showOutput"
              @pointerup="scheduleHide"
            >
              <input
                v-bind="api.getHiddenInputProps({ index })"
                type="range"
                min="0"
                :max="maxIndex"
                step="1"
              >
            </div>
          </div>
          <div v-bind="api.getMarkerGroupProps()">
            <div
              v-for="(item, index) in labelList"
              v-bind="api.getMarkerProps({ value: index })"
              :key="index"
            >
              <span :data-length="shouldShowLabel(index) ? 'long' : 'short'" />
              <p v-if="shouldShowLabel(index)">
                {{ dayjs.unix(item).tz('Asia/Tokyo').format('H:mm') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.slider__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 5px 5px;
  background-color: #eef1f8;
  gap: 16px;
  width: 100%;
  position: relative;
  padding-bottom: 17px !important;
  gap: 7px !important;
}

.controller {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 100%;
  user-select: none;
  position: relative;
}

.controller__button {
  display: flex;
  align-items: center;
  gap: 5px;
  user-select: none;
  p {
    min-width: 150px;
    padding: 6px 15px 5px;
    background-color: #fff;
    border-radius: 100vmax;
    color: #303030;
    font-size: 14px;
    line-height: 14px;
    text-align: center;
    font-weight: 600;
  }
}

.anime__button {
  position: relative;
  top: 0;
  left: 0;
  margin-top: -4px;
}

.content {
  display: flex;
  gap: 10px;
  width: 100%;
  position: relative;
  gap: 20px;
  z-index: 2;
}

button {
  width: 25px;
  height: 25px;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 2px 0 rgb(0 0 0 / 0.15);
  background-position: center;
  background-repeat: no-repeat;
  background-size: 25px;
  cursor: pointer;
  touch-action: manipulation;
  &[data-button="prev"] {
    background-size: 20px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/time_prev.svg");
  }
  &[data-button="next"] {
    background-size: 20px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/time_next.svg");
  }
  &[data-button="prevDay"] {
    background-size: 20px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/day_prev.svg");
  }
  &[data-button="nextDay"] {
    background-size: 20px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/day_next.svg");
  }
  &[data-button="play"] {
    background-size: 12px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_play.svg");
  }
  &[data-button="stop"] {
    background-size: 12px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_pause.svg");
  }
  &:disabled {
    opacity: 0.4;
    pointer-events: none;
  }
  @media (any-hover: hover) {
    transition: opacity .2s ease-in-out;
    &:hover {
      opacity: .6;
    }
  }
}

.range {
  flex: 1;
  padding-top: 5px;
  [data-part="control"] {
    position: relative;
  }
  [data-part="track"] {
    position: relative;
    height: 7px;
    background-color: #fff;
    overflow: visible;
  }
  [data-part="track"]::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
  }
  [data-part="range"] {
    height: 100%;
    background-color: #fff;
  }
  [data-part="thumb"] {
    position: absolute;
    top: 50%;
    width: 10px;
    height: 20px;
    background-color: #3569C0;
    border-radius: 100vmax;
    cursor: grab;
    transform: translate(-50%, -50%) !important;
    z-index: 3;
    &[data-dragging] {
      cursor: grabbing;
    }
    output {
      display: flex;
      flex-direction: column;
      gap: 3px;
      position: absolute;
      top: -60px;
      left: 50%;
      transform: translateX(-50%);
      padding: 7px 10px;
      border-radius: 4px;
      background-color: #fff;
      border: 1px solid #3569C0;
      filter: drop-shadow(0 2px 4px rgb(0 0 0 / .33));
      white-space: nowrap;
      max-width: calc(100vw - 40px);
      transition: opacity .3s ease-in-out;
      pointer-events: none;
      &::before,
      &::after {
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        height: 0;
        width: 0;
        border: solid transparent;
        pointer-events: none;
        content: "";
      }
      &::before {
        border-top-color: #3569C0;
        border-width: 25px 5px;
      }
      &::after {
        border-top-color: #fff;
        border-width: 22px 4px;
      }
      &.hidden {
        opacity: 0;
      }
    }
  }
  [data-part="marker-group"] {
    transform: translateY(-11px);
  }
  [data-part="marker"] {
    display: flex;
    flex-direction: column;
    gap: 6px;
    span {
      display: block;
      margin-inline: auto;
      width: 1px;
      background-color: #343D53;
      border-radius: 100vmax;
      z-index: 1;
      &[data-length="short"] {
        height: 5px;
        margin-top: 5px;
      }
      &[data-length="middle"] {
        height: 9px;
        margin-top: 3px;
      }
      &[data-length="long"] {
        height: 15px;
      }
    }
    p {
      text-align: center;
      color: #303030;
      font-size: 13px;
      font-weight: 300;
      line-height: 1em;
      white-space: nowrap;
    }
  }
}

@media screen and (min-width: 830px) {
  .content {
    gap: 20px;
  }
  .slider__inner {
    padding: 6px 10px 5px;
  }
}
</style>
